<script setup lang="ts">
/**
 * Knowledge overview: search first, then the map (lineages → environments →
 * what runs there), browse by type and tag, what changed, my pages, and — for
 * writers — the gaps worth closing. One aggregate call (GET /knowledge/overview).
 * Old links with ?q= / ?types= land on the results page.
 */
import { Activity, AlertTriangle, ArrowRight, Clock, FilePlus, Hash, Inbox, Layers, LayoutGrid, SearchX, Sparkles, Upload, UserRound, UserX } from "@lucide/vue";
import { computed, ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import { isNotAvailable } from "@/api/client";
import { useKnowledgeOverview } from "@/api/knowledge";
import EmptyState from "@/components/common/EmptyState.vue";
import ErrorState from "@/components/common/ErrorState.vue";
import ListSkeleton from "@/components/common/ListSkeleton.vue";
import PageHeader from "@/components/common/PageHeader.vue";
import RelativeTime from "@/components/common/RelativeTime.vue";
import SectionCard from "@/components/common/SectionCard.vue";
import ChangeFeedItem from "@/components/knowledge/ChangeFeedItem.vue";
import CreateEntityDialog from "@/components/knowledge/CreateEntityDialog.vue";
import EntityLink from "@/components/knowledge/EntityLink.vue";
import KnowledgeNav from "@/components/knowledge/KnowledgeNav.vue";
import KnowledgeSearchBox from "@/components/knowledge/KnowledgeSearchBox.vue";
import LineageCard from "@/components/knowledge/LineageCard.vue";
import UploadDialog from "@/components/knowledge/UploadDialog.vue";
import { useTriageSummary } from "@/components/knowledge/inboxTriage";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { useSession } from "@/composables/useSession";
import { t } from "@/copy/strings";
import { formatNumber } from "@/lib/format";
import { DOCUMENT_INFO, ENTITY_TYPES } from "@/lib/knowledge";

definePage({ meta: { title: "Knowledge", layout: "wide", requires: { capability: "knowledge.read" } } });

const route = useRoute();
const router = useRouter();
const { can } = useSession();

// Deep links from before the overview (/knowledge?q=…, ?types=…) belong to the results page.
const LEGACY = ["q", "types", "tags", "lineage", "sources", "status"];
if (LEGACY.some((k) => route.query[k])) void router.replace({ path: "/knowledge/search", query: route.query });

const draft = ref("");
const overview = useKnowledgeOverview();
const data = computed(() => overview.data.value);

const browseTypes = computed(() => {
  const byType = data.value?.counts.byType ?? {};
  const types = ENTITY_TYPES.map((x) => ({ ...x, count: byType[x.type] ?? 0 })).filter((x) => x.count > 0);
  const docs = data.value?.counts.documents ?? 0;
  return docs > 0 ? [...types, { ...DOCUMENT_INFO, count: docs }] : types;
});
const maxTag = computed(() => Math.max(1, ...(data.value?.tags ?? []).map((x) => x.count)));
/** Four steps of emphasis in the tag index, by frequency. */
function tagWeight(count: number) {
  const r = count / maxTag.value;
  return r > 0.66 ? "text-base font-semibold" : r > 0.33 ? "text-sm font-semibold" : r > 0.12 ? "text-sm font-medium" : "text-xs font-medium";
}

const INBOX_KINDS: Record<string, string> = { proposal: "Suggestions", conflict: "Conflicts", duplicate: "Possible duplicates", feedback: "Feedback" };
const attention = computed(() => data.value?.attention ?? null);
const attentionEmpty = computed(() => {
  const a = attention.value;
  return Boolean(a && !a.inbox?.total && !a.missingOwner.count && !a.stale.count && !a.zeroResultQueries.length);
});
// The inbox opens on the likely noise (the triage's reject verdict): the quickest win, decided in groups.
const triage = useTriageSummary(() => can("knowledge.admin") && Boolean(attention.value?.inbox?.total));
const noise = computed(() => triage.data.value?.byVerdict.reject ?? 0);
const inboxLink = computed(() => (noise.value > 0 ? { path: "/knowledge/inbox", query: { verdict: "reject" } } : { path: "/knowledge/inbox" }));
const mine = computed(() => data.value?.mine);
const mineEmpty = computed(() => !mine.value?.owned.length && !mine.value?.edited.length);

// Create a page (also prefilled from a query nobody found)
const createOpen = ref(false);
const createName = ref("");
function createFor(name = "") {
  createName.value = name;
  createOpen.value = true;
}

// Upload a file as a knowledge document (PDF, Markdown, images, …): the dialog follows its conversion.
const uploadOpen = ref(false);
</script>

<template>
  <div>
    <PageHeader :title="t.knowledge.title" :description="t.knowledge.overviewIntro">
      <template #actions>
        <Button as-child variant="ghost"><RouterLink to="/ask"><Sparkles /> {{ t.knowledge.askTitle }}</RouterLink></Button>
        <template v-if="can('knowledge.write')">
          <Button variant="outline" @click="uploadOpen = true"><Upload /> {{ t.knowledgeUpload.button }}</Button>
          <Button @click="createFor()"><FilePlus /> {{ t.knowledge.create }}</Button>
        </template>
      </template>
    </PageHeader>
    <KnowledgeNav />

    <KnowledgeSearchBox v-model="draft" autofocus class="mb-8" />

    <ErrorState v-if="overview.error.value && !isNotAvailable(overview.error.value)" :error="overview.error.value" @retry="overview.refetch()" />
    <p v-else-if="overview.error.value" class="text-sm text-muted-foreground">{{ t.errors.notAvailableBody }}</p>

    <div v-else class="grid items-start gap-6 xl:grid-cols-[minmax(0,1fr)_22rem] 2xl:grid-cols-[minmax(0,1fr)_26rem]">
      <!-- Main column: the map -->
      <div class="flex min-w-0 flex-col gap-8">
        <section aria-labelledby="k-lineages">
          <div class="mb-3 flex items-baseline justify-between gap-2">
            <h2 id="k-lineages" class="section-title flex items-center gap-2"><Layers class="size-4 text-muted-foreground" aria-hidden="true" /> {{ t.knowledge.lineageMap }}</h2>
            <p class="hidden text-xs text-muted-foreground sm:block">{{ t.knowledge.lineageMapHint }}</p>
          </div>
          <div v-if="overview.isPending.value" class="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            <Skeleton v-for="i in 3" :key="i" class="h-40 rounded-xl" />
          </div>
          <EmptyState v-else-if="!data?.lineages.length" compact :icon="Layers" :title="t.knowledge.lineageMapEmpty" />
          <ul v-else class="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            <li v-for="l in data.lineages" :key="l.slug"><LineageCard :lineage="l" /></li>
          </ul>
        </section>

        <section v-if="overview.isPending.value || browseTypes.length" aria-labelledby="k-types">
          <h2 id="k-types" class="section-title mb-3 flex items-center gap-2"><LayoutGrid class="size-4 text-muted-foreground" aria-hidden="true" /> {{ t.knowledge.browseByType }}</h2>
          <div v-if="overview.isPending.value" class="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4">
            <Skeleton v-for="i in 8" :key="i" class="h-10 rounded-lg" />
          </div>
          <ul v-else class="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4 2xl:grid-cols-5">
            <li v-for="x in browseTypes" :key="x.type">
              <Card variant="interactive" padding="none" as-child class="flex-row items-center gap-2 rounded-lg px-3 py-2 text-sm">
                <RouterLink :to="{ path: '/knowledge/search', query: { types: x.type } }">
                  <component :is="x.icon" class="size-4 shrink-0" :style="{ color: x.color }" aria-hidden="true" />
                  <span class="flex-1 truncate font-medium">{{ x.plural }}</span>
                  <span class="text-xs text-muted-foreground tabular-nums">{{ formatNumber(x.count) }}</span>
                </RouterLink>
              </Card>
            </li>
          </ul>
        </section>

        <section aria-labelledby="k-tags">
          <h2 id="k-tags" class="section-title mb-3 flex items-center gap-2"><Hash class="size-4 text-muted-foreground" aria-hidden="true" /> {{ t.knowledge.tagIndex }}</h2>
          <Skeleton v-if="overview.isPending.value" class="h-16 rounded-lg" />
          <p v-else-if="!data?.tags.length" class="text-sm text-muted-foreground">{{ t.knowledge.tagIndexEmpty }}</p>
          <Card v-else as="ul" padding="none" class="flex-row flex-wrap items-baseline gap-x-3 gap-y-1.5 px-4 py-3">
            <li v-for="tag in data.tags" :key="tag.tag">
              <RouterLink
                :to="{ path: '/knowledge/search', query: { tags: tag.tag } }"
                :class="['text-muted-foreground transition-colors hover:text-primary', tagWeight(tag.count)]"
                :title="`${tag.count} pages and documents`"
              >
                #{{ tag.tag }}<span class="ml-0.5 align-super text-[10px] font-normal opacity-70">{{ tag.count }}</span>
              </RouterLink>
            </li>
          </Card>
        </section>

        <SectionCard :title="t.knowledge.recentlyChanged" :icon="Activity" to="/knowledge/changes" :link-label="t.knowledge.changeLog">
          <ListSkeleton v-if="overview.isPending.value" :rows="4" avatar />
          <p v-else-if="!data?.recent.length" class="text-sm text-muted-foreground">{{ t.knowledge.recentlyChangedEmpty }}</p>
          <ol v-else class="-my-1 divide-y">
            <li v-for="cs in data.recent.slice(0, 8)" :key="cs.changeSetId"><ChangeFeedItem :change="cs" /></li>
          </ol>
        </SectionCard>
      </div>

      <!-- Side column: what needs me -->
      <div class="flex min-w-0 flex-col gap-6">
        <SectionCard v-if="attention" :title="t.knowledge.needsAttention" :icon="AlertTriangle" :description="t.knowledge.needsAttentionHint">
          <p v-if="attentionEmpty" class="text-sm text-muted-foreground">{{ t.knowledge.allGood }}</p>
          <div v-else class="flex flex-col gap-5">
            <div v-if="attention.inbox && attention.inbox.total > 0">
              <p class="mb-1.5 flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
                <Inbox class="size-3.5" aria-hidden="true" />
                <RouterLink :to="inboxLink" class="hover:text-foreground hover:underline">{{ t.knowledge.inboxWaiting }}</RouterLink>
                <span class="ml-auto tabular-nums">{{ formatNumber(attention.inbox.total) }}</span>
              </p>
              <RouterLink
                v-if="noise > 0"
                :to="inboxLink"
                class="group mb-2 flex items-center gap-2 rounded-lg bg-info-soft px-2.5 py-2 text-sm text-info transition-colors hover:bg-info-soft/70"
              >
                <span class="min-w-0 flex-1">{{ t.knowledge.inboxNoise(formatNumber(noise)) }}</span>
                <ArrowRight class="size-4 shrink-0 transition-transform group-hover:translate-x-0.5" aria-hidden="true" />
              </RouterLink>
              <ul class="flex flex-col gap-0.5">
                <li v-for="k in attention.inbox.byKind" :key="k.kind">
                  <RouterLink :to="{ path: '/knowledge/inbox', query: { kind: k.kind } }" class="-mx-1.5 flex items-center gap-2 rounded px-1.5 py-1 text-sm hover:bg-muted">
                    <span class="flex-1">{{ INBOX_KINDS[k.kind] ?? k.kind }}</span>
                    <span :class="['rounded-full px-1.5 text-[11px] font-semibold tabular-nums', k.kind === 'conflict' ? 'bg-warning-soft text-warning-foreground' : 'bg-info-soft text-info']">
                      {{ formatNumber(k.count) }}
                    </span>
                  </RouterLink>
                </li>
              </ul>
            </div>

            <div v-if="attention.missingOwner.count > 0">
              <p class="mb-1.5 flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
                <UserX class="size-3.5" aria-hidden="true" /> {{ t.knowledge.missingOwner }}
                <span class="ml-auto tabular-nums">{{ formatNumber(attention.missingOwner.count) }}</span>
              </p>
              <ul class="flex flex-col">
                <li v-for="e in attention.missingOwner.items" :key="e.uri"><EntityLink :entity="e" /></li>
              </ul>
            </div>

            <div v-if="attention.stale.count > 0">
              <p class="mb-1.5 flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
                <Clock class="size-3.5" aria-hidden="true" /> {{ t.knowledge.stalePages(attention.stale.days) }}
                <span class="ml-auto tabular-nums">{{ formatNumber(attention.stale.count) }}</span>
              </p>
              <ul class="flex flex-col">
                <li v-for="e in attention.stale.items" :key="e.uri">
                  <EntityLink :entity="e"><span class="shrink-0 text-xs text-muted-foreground"><RelativeTime :value="e.updatedAt" /></span></EntityLink>
                </li>
              </ul>
            </div>

            <div v-if="attention.zeroResultQueries.length">
              <p class="mb-0.5 flex items-center gap-1.5 text-xs font-medium text-muted-foreground"><SearchX class="size-3.5" aria-hidden="true" /> {{ t.knowledge.zeroResults }}</p>
              <p class="mb-1.5 text-xs text-muted-foreground">{{ t.knowledge.zeroResultsHint }}</p>
              <ul class="flex flex-col gap-0.5">
                <li v-for="z in attention.zeroResultQueries" :key="z.query" class="flex items-center gap-2">
                  <RouterLink :to="{ path: '/knowledge/search', query: { q: z.query } }" class="min-w-0 flex-1 truncate rounded text-sm hover:text-primary hover:underline">
                    “{{ z.query }}”
                  </RouterLink>
                  <span class="text-xs text-muted-foreground tabular-nums" :title="`${z.count} searches`">{{ z.count }}×</span>
                  <Button size="icon-sm" variant="ghost" :aria-label="t.knowledge.createPageFor(z.query)" :title="t.knowledge.createPageFor(z.query)" @click="createFor(z.query)">
                    <FilePlus />
                  </Button>
                </li>
              </ul>
            </div>
          </div>
        </SectionCard>

        <SectionCard :title="t.knowledge.mine" :icon="UserRound" :to="mine?.person ? `/k/person/${encodeURIComponent(mine.person.slug)}` : undefined" :link-label="mine?.person ? 'Your page' : undefined">
          <ListSkeleton v-if="overview.isPending.value" :rows="3" />
          <template v-else-if="mine">
            <p v-if="mineEmpty" class="text-sm text-muted-foreground">{{ t.knowledge.mineEmpty }}</p>
            <div v-else class="flex flex-col gap-4">
              <div v-if="mine.owned.length">
                <p class="mb-1 text-xs font-medium text-muted-foreground">{{ t.knowledge.mineOwned }}</p>
                <ul class="flex flex-col">
                  <li v-for="e in mine.owned" :key="e.uri"><EntityLink :entity="e" /></li>
                </ul>
              </div>
              <div v-if="mine.edited.length">
                <p class="mb-1 text-xs font-medium text-muted-foreground">{{ t.knowledge.mineEdited }}</p>
                <ul class="flex flex-col">
                  <li v-for="e in mine.edited" :key="e.uri">
                    <EntityLink :entity="e"><span class="shrink-0 text-xs text-muted-foreground"><RelativeTime :value="e.updatedAt" /></span></EntityLink>
                  </li>
                </ul>
              </div>
            </div>
            <p v-if="!mine.person && can('knowledge.write')" class="mt-3 text-xs text-muted-foreground">{{ t.knowledge.mineNoPerson }}</p>
          </template>
        </SectionCard>
      </div>
    </div>

    <CreateEntityDialog v-model:open="createOpen" :initial-name="createName" />
    <UploadDialog v-model:open="uploadOpen" />
  </div>
</template>
